import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { searchProducts, getProducts } from "../../services/productApi";
import "./shop.css";

const ShopHeader = ({
  categories = [],
  onCategoryClick,
  selectedCategory,
}) => {
  const navigate = useNavigate();
  const searchRef = useRef(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [trending, setTrending] = useState([]);
  const [recent, setRecent] = useState([]);

  const [activeIndex, setActiveIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);

  const timeoutRef = useRef(null);

  // ================= OUTSIDE CLICK =================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ================= RECENT + TRENDING =================
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("recentSearches") || "[]");
    setRecent(stored);

    const fetchTrending = async () => {
      try {
        const data = await getProducts();
        setTrending(data.slice(0, 5));
      } catch (err) {
        console.log(err);
      }
    };

    fetchTrending();
  }, []);

  const saveRecent = (product) => {
    const item = { id: product.id, name: product.name };

    const updated = [
      item,
      ...recent.filter((r) => r.id !== item.id),
    ].slice(0, 5);

    setRecent(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
  };

  // ================= SEARCH =================
  const handleSearch = (value) => {
    setQuery(value);
    setShowDropdown(true);

    clearTimeout(timeoutRef.current);

    if (!value.trim()) {
      setResults([]);
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      try {
        const data = await searchProducts(value);
        setResults(Array.isArray(data) ? data : data.products || []);
        setActiveIndex(-1);
      } catch (err) {
        console.log(err);
      }
    }, 300);
  };

  // ================= KEYBOARD =================
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      setActiveIndex((p) => (p < results.length - 1 ? p + 1 : 0));
    }

    if (e.key === "ArrowUp") {
      setActiveIndex((p) => (p > 0 ? p - 1 : results.length - 1));
    }

    if (e.key === "Enter") {
      if (activeIndex >= 0 && results[activeIndex]) {
        handleSelect(results[activeIndex]);
      } else if (query.trim()) {
        navigate(`/search?q=${query}`);
        setShowDropdown(false);
      }
    }
  };

  const handleSelect = (p) => {
    saveRecent(p);
    navigate(`/product/${p.id}`);
    setQuery("");
    setShowDropdown(false);
  };

  const showResults = query.trim().length > 0 && results.length > 0;

  return (
    <>
      {/* ================= HEADER ================= */}
      <div className="top-header">

        {/* LOGO */}
        <div className="logo" onClick={() => navigate("/shop")}>
          Kuhu.in
        </div>

        {/* HOME BUTTON */}
        <button
          className="home-btn"
          onClick={() => navigate("/shop")}
        >
          🏠 Home
        </button>

        {/* LOCATION */}
        <div className="location">
          📍 Delivering to New Delhi 110003
        </div>

        {/* SEARCH */}
        <div className="search" ref={searchRef}>
          <input
            placeholder="Search products..."
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowDropdown(true)}
          />

          <button
            onClick={() => {
              if (query.trim()) {
                navigate(`/search?q=${query}`);
                setShowDropdown(false);
              }
            }}
          >
            🔍
          </button>

          {/* DROPDOWN */}
          {showDropdown && (
            <div className="search-dropdown">

              {!query && recent.length > 0 && (
                <div className="dropdown-section">
                  <div className="search-title">Recent Searches</div>
                  {recent.map((p) => (
                    <div
                      key={p.id}
                      className="search-item"
                      onClick={() => navigate(`/product/${p.id}`)}
                    >
                      {p.name}
                    </div>
                  ))}
                </div>
              )}

              {!query && recent.length === 0 && (
                <div className="dropdown-section">
                  <div className="search-title">Trending</div>
                  {trending.map((p) => (
                    <div
                      key={p.id}
                      className="search-item"
                      onClick={() => handleSelect(p)}
                    >
                      {p.name}
                    </div>
                  ))}
                </div>
              )}

              {showResults && (
                <div className="dropdown-section">
                  <div className="search-title">Suggestions</div>

                  {results.map((p, i) => (
                    <div
                      key={p.id}
                      className={`search-item ${i === activeIndex ? "active" : ""
                        }`}
                      onClick={() => handleSelect(p)}
                    >
                      {p.name}
                    </div>
                  ))}
                </div>
              )}

              {query && results.length === 0 && (
                <div className="search-item empty">
                  No products found
                </div>
              )}
            </div>
          )}
        </div>

        {/* MENU */}
        <div className="menu">
          <span onClick={() => navigate("/login")}>Sign in</span>

          {/* 🔥 NEW ADDITION */}
          <span onClick={() => navigate("/track-order")}>
            📦 Track Order
          </span>
          {/* ✅ NEW ADDITION (Wishlist) */}
          <span onClick={() => navigate("/wishlist")}>
            ❤️ Wishlist
          </span>
          <span onClick={() => navigate("/cart")}>Cart 🛒</span>
        </div>
      </div>

      {/* CATEGORY BAR */}
      <div className="category-bar">
        <button
          className={`cat-btn ${selectedCategory === "All" ? "active" : ""
            }`}
          onClick={() => onCategoryClick("All")}
        >
          All
        </button>

        {categories.map((c) => (
          <button
            key={c}
            className={`cat-btn ${selectedCategory === c ? "active" : ""
              }`}
            onClick={() => onCategoryClick(c)}
          >
            {c}
          </button>
        ))}
      </div>
    </>
  );
};

export default ShopHeader;